import React, { useState, useEffect } from 'react';
import { useTCoins, TCOINS_TYPES, TCOINS_COLORS, TCOINS_ICONS, TCOINS_NAMES } from '../../context/TCoinsContext';
import { collection, getDocs, doc, updateDoc, increment, addDoc, getDoc, setDoc } from 'firebase/firestore';
import { db, storage } from '../../firebase/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const CoinsManagement = ({ className = "" }) => {
  const { addTCoins, deductTCoins } = useTCoins();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState('add');
  const [coinType, setCoinType] = useState(TCOINS_TYPES.VIRTUAL);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [exchangeRequests, setExchangeRequests] = useState([]);
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [selectedExchangeRequest, setSelectedExchangeRequest] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState({
    bank_transfer: {
      id: 'bank_transfer',
      name: 'โอนเงินผ่านธนาคาร',
      icon: '🏦',
      color: 'green',
      enabled: true,
      type: 'bank',
      bankName: 'ธนาคารกรุงไทย',
      accountNumber: '123-4-56789-0',
      accountName: 'บริษัท ฮักกัน จำกัด'
    },
    promptpay: {
      id: 'promptpay',
      name: 'พร้อมเพย์',
      icon: '📱',
      color: 'blue',
      enabled: true,
      type: 'mobile',
      phoneNumber: '081-234-5678',
      accountName: 'บริษัท ฮักกัน จำกัด'
    },
    true_wallet: {
      id: 'true_wallet',
      name: 'ทรูวอลเล็ท',
      icon: '💳',
      color: 'purple',
      enabled: true,
      type: 'wallet',
      phoneNumber: '081-234-5678',
      accountName: 'บริษัท ฮักกัน จำกัด'
    }
  });
  const [packageConfig, setPackageConfig] = useState({
    starter: { 
      name: 'Starter Pack',
      description: 'แพ็กเกจเริ่มต้น',
      price: 100,
      tcoins: 100,
      bonusType: 'virtual', 
      bonusAmount: 0,
      color: 'silver',
      popular: false,
      enabled: true
    },
    popular: { 
      name: 'Popular Pack',
      description: 'แพ็กเกจยอดนิยม',
      price: 500,
      tcoins: 500,
      bonusType: 'virtual', 
      bonusAmount: 25,
      color: 'gold',
      popular: true,
      enabled: true
    },
    premium: { 
      name: 'Premium Pack',
      description: 'แพ็กเกจพรีเมียม',
      price: 1000,
      tcoins: 1000,
      bonusType: 'virtual', 
      bonusAmount: 100,
      color: 'emerald',
      popular: false,
      enabled: true
    },
    vip: { 
      name: 'VIP Pack',
      description: 'แพ็กเกจ VIP',
      price: 5000,
      tcoins: 5000,
      bonusType: 'virtual', 
      bonusAmount: 750,
      color: 'onyx',
      popular: false,
      enabled: true
    },
    legendary: { 
      name: 'Legendary Pack',
      description: 'แพ็กเกจตำนาน',
      price: 10000,
      tcoins: 10000,
      bonusType: 'virtual', 
      bonusAmount: 2000,
      color: 'purple',
      popular: false,
      enabled: true
    }
  });
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [isEditingPackages, setIsEditingPackages] = useState(false);
  const [isEditingVerification, setIsEditingVerification] = useState(false);
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [verificationConfig, setVerificationConfig] = useState({
    initialCost: 500,
    renewalCost: 300
  });
  const [uploadingQRCode, setUploadingQRCode] = useState({}); // Track QR code uploads per method
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    id: '',
    name: '',
    icon: '💰',
    color: 'gray',
    enabled: true,
    type: 'bank', // bank, mobile, wallet, other
    fields: {}
  });

  const loadPaymentConfig = async () => {
    try {
      const configRef = doc(db, 'config', 'payment');
      const configSnap = await getDoc(configRef);
      
      if (configSnap.exists()) {
        setPaymentConfig(configSnap.data());
      }
    } catch (error) {
      console.error('Error loading payment config:', error);
    }
  };

  const loadPackageConfig = async () => {
    try {
      const configRef = doc(db, 'config', 'packages');
      const configSnap = await getDoc(configRef);
      
      if (configSnap.exists()) {
        setPackageConfig(configSnap.data());
      }
    } catch (error) {
      console.error('Error loading package config:', error);
    }
  };

  const loadVerificationConfig = async () => {
    try {
      const configRef = doc(db, 'config', 'verification');
      const configSnap = await getDoc(configRef);
      
      if (configSnap.exists()) {
        setVerificationConfig(configSnap.data());
      }
    } catch (error) {
      console.error('Error loading verification config:', error);
    }
  };

  useEffect(() => {
    loadUsers();
    loadExchangeRequests();
    loadPurchaseRequests();
    loadPaymentConfig();
    loadPackageConfig();
    loadVerificationConfig();
  }, []);

  const saveVerificationConfig = async () => {
    try {
      const configRef = doc(db, 'config', 'verification');
      await setDoc(configRef, verificationConfig, { merge: true });
      
      // Refresh cache in verificationModels (force reload)
      const { loadVerificationCostConfig } = await import('../../data/verificationModels');
      await loadVerificationCostConfig(true);
      
      alert('บันทึกการตั้งค่าค่าใช้จ่ายการตรวจสอบร้านค้าสำเร็จ!');
      setIsEditingVerification(false);
    } catch (error) {
      console.error('Error saving verification config:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    }
  };

  const handleQRCodeUpload = async (methodId, file) => {
    if (!file) return;
    
    // Validate file
    if (!file.type.startsWith('image/')) {
      alert('กรุณาเลือกไฟล์รูปภาพ');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
      return;
    }

    setUploadingQRCode({ ...uploadingQRCode, [methodId]: true });
    
    try {
      // Upload QR Code to Firebase Storage
      const fileName = `payment-qr-codes/${methodId}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update payment config with QR code URL
      setPaymentConfig({
        ...paymentConfig,
        [methodId]: {
          ...paymentConfig[methodId],
          qrCodeUrl: downloadURL
        }
      });
      
      alert('อัพโหลด QR Code สำเร็จ!');
    } catch (error) {
      console.error('Error uploading QR code:', error);
      alert('เกิดข้อผิดพลาดในการอัพโหลด QR Code');
    } finally {
      setUploadingQRCode({ ...uploadingQRCode, [methodId]: false });
    }
  };

  const removeQRCode = (methodId) => {
    if (window.confirm('ต้องการลบ QR Code นี้หรือไม่?')) {
      setPaymentConfig({
        ...paymentConfig,
        [methodId]: {
          ...paymentConfig[methodId],
          qrCodeUrl: null
        }
      });
    }
  };

  const savePaymentConfig = async () => {
    try {
      const configRef = doc(db, 'config', 'payment');
      await setDoc(configRef, paymentConfig, { merge: true });
      alert('บันทึกข้อมูลการชำระเงินสำเร็จ!');
      setIsEditingPayment(false);
    } catch (error) {
      console.error('Error saving payment config:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    }
  };

  const addPaymentMethod = () => {
    if (!newPaymentMethod.id || !newPaymentMethod.name) {
      alert('กรุณากรอก ID และชื่อช่องทางการชำระเงิน');
      return;
    }

    if (paymentConfig[newPaymentMethod.id]) {
      alert('ID นี้มีอยู่แล้ว กรุณาใช้ ID อื่น');
      return;
    }

    const methodId = newPaymentMethod.id;
    setPaymentConfig({
      ...paymentConfig,
      [methodId]: {
        ...newPaymentMethod,
        fields: getDefaultFields(newPaymentMethod.type)
      }
    });

    setNewPaymentMethod({
      id: '',
      name: '',
      icon: '💰',
      color: 'gray',
      enabled: true,
      type: 'bank',
      fields: {}
    });
    setShowAddPaymentMethod(false);
  };

  const removePaymentMethod = (methodId) => {
    if (Object.keys(paymentConfig).length <= 1) {
      alert('ต้องมีช่องทางการชำระเงินอย่างน้อย 1 ช่องทาง');
      return;
    }

    if (window.confirm(`ต้องการลบช่องทางการชำระเงิน "${paymentConfig[methodId].name}" หรือไม่?`)) {
      const newConfig = { ...paymentConfig };
      delete newConfig[methodId];
      setPaymentConfig(newConfig);
    }
  };

  const togglePaymentMethod = (methodId) => {
    setPaymentConfig({
      ...paymentConfig,
      [methodId]: {
        ...paymentConfig[methodId],
        enabled: !paymentConfig[methodId].enabled
      }
    });
  };

  const getDefaultFields = (type) => {
    switch (type) {
      case 'bank':
        return {
          bankName: '',
          accountNumber: '',
          accountName: ''
        };
      case 'mobile':
        return {
          phoneNumber: '',
          accountName: ''
        };
      case 'wallet':
        return {
          phoneNumber: '',
          accountName: ''
        };
      default:
        return {
          accountName: '',
          accountNumber: ''
        };
    }
  };

  const getPaymentMethodColor = (color) => {
    const colors = {
      green: 'bg-green-50 border-green-200 text-green-800',
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      gray: 'bg-gray-50 border-gray-200 text-gray-800'
    };
    return colors[color] || colors.gray;
  };

  const savePackageConfig = async () => {
    try {
      const configRef = doc(db, 'config', 'packages');
      await setDoc(configRef, packageConfig, { merge: true });
      alert('บันทึกการตั้งค่าแพ็คเกจสำเร็จ!');
      setIsEditingPackages(false);
    } catch (error) {
      console.error('Error saving package config:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);
      
      const usersList = [];
      usersSnap.forEach((doc) => {
        const userData = doc.data();
        usersList.push({
          id: doc.id,
          ...userData,
          totalTCoins: (userData.tcoins?.virtual || 0) + (userData.tcoins?.real || 0),
          totalVirtual: userData.tcoins?.virtual || 0,
          totalReal: userData.tcoins?.real || 0
        });
      });
      
      setUsers(usersList.sort((a, b) => b.totalTCoins - a.totalTCoins));
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExchangeRequests = async () => {
    try {
      const exchangeRef = collection(db, 'exchangeRequests');
      const exchangeSnap = await getDocs(exchangeRef);
      
      const requests = [];
      exchangeSnap.forEach((doc) => {
        requests.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setExchangeRequests(requests.sort((a, b) => b.createdAt - a.createdAt));
    } catch (error) {
      console.error('Error loading exchange requests:', error);
    }
  };

  const loadPurchaseRequests = async () => {
    try {
      const purchaseRef = collection(db, 'purchaseRequests');
      const purchaseSnap = await getDocs(purchaseRef);
      
      const requests = [];
      purchaseSnap.forEach((doc) => {
        requests.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setPurchaseRequests(requests.sort((a, b) => b.createdAt - a.createdAt));
    } catch (error) {
      console.error('Error loading purchase requests:', error);
    }
  };

  const handleCoinsAction = async () => {
    if (!selectedUser || !amount || !reason) {
      alert('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    setIsProcessing(true);
    try {
      const amountNum = parseInt(amount);
      if (amountNum <= 0) {
        alert('จำนวนเหรียญต้องมากกว่า 0');
        return;
      }

      let success = false;
      if (actionType === 'add') {
        success = await addTCoins(selectedUser.id, coinType, amountNum, reason);
      } else {
        success = await deductTCoins(selectedUser.id, coinType, amountNum, reason);
      }

      if (success) {
        alert(`${actionType === 'add' ? 'เพิ่ม' : 'หัก'}เหรียญสำเร็จ!`);
        setAmount('');
        setReason('');
        loadUsers();
      } else {
        alert('เกิดข้อผิดพลาดในการจัดการเหรียญ');
      }
    } catch (error) {
      console.error('Error handling coins action:', error);
      alert('เกิดข้อผิดพลาดในการจัดการเหรียญ');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExchangeRequest = async (requestId, action) => {
    try {
      const requestRef = doc(db, 'exchangeRequests', requestId);
      await updateDoc(requestRef, {
        status: action,
        processedAt: new Date(),
        processedBy: 'admin'
      });

      if (action === 'approved') {
        // หัก T Coins จากผู้ใช้
        const request = exchangeRequests.find(r => r.id === requestId);
        await deductTCoins(request.userId, TCOINS_TYPES.REAL, request.amount, 'แลกเป็นเงินบาท');
      }

      alert(`คำขอแลกเหรียญ${action === 'approved' ? 'อนุมัติ' : 'ปฏิเสธ'}แล้ว`);
      loadExchangeRequests();
      loadUsers();
    } catch (error) {
      console.error('Error handling exchange request:', error);
      alert('เกิดข้อผิดพลาดในการจัดการคำขอแลกเหรียญ');
    }
  };

  const handlePurchaseRequest = async (requestId, action) => {
    try {
      const requestRef = doc(db, 'purchaseRequests', requestId);
      await updateDoc(requestRef, {
        status: action,
        processedAt: new Date(),
        processedBy: 'admin'
      });

      if (action === 'approved') {
        // เพิ่ม T Coins ให้ผู้ใช้
        const request = purchaseRequests.find(r => r.id === requestId);
        await addTCoins(request.userId, TCOINS_TYPES.REAL, request.amount, 'ซื้อเหรียญจากแอดมิน');
      }

      alert(`คำขอซื้อเหรียญ${action === 'approved' ? 'อนุมัติ' : 'ปฏิเสธ'}แล้ว`);
      loadPurchaseRequests();
      loadUsers();
    } catch (error) {
      console.error('Error handling purchase request:', error);
      alert('เกิดข้อผิดพลาดในการจัดการคำขอซื้อเหรียญ');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'รอยืนยัน';
      case 'approved': return 'อนุมัติแล้ว';
      case 'rejected': return 'ปฏิเสธ';
      default: return 'ไม่ทราบ';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">🪙 จัดการเหรียญในระบบ</h2>

      {/* สถิติเหรียญรวม */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-r from-red-400 to-red-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Bonus Coins</p>
              <p className="text-2xl font-bold">
                {users.reduce((sum, user) => sum + user.totalVirtual, 0)}
              </p>
            </div>
            <div className="text-4xl">🎁</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-400 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">T Coins</p>
              <p className="text-2xl font-bold">
                {users.reduce((sum, user) => sum + user.totalReal, 0)}
              </p>
            </div>
            <div className="text-4xl">💰</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-400 to-blue-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">คำขอซื้อเหรียญ</p>
              <p className="text-2xl font-bold">
                {purchaseRequests.filter(r => r.status === 'pending').length}
              </p>
            </div>
            <div className="text-4xl">💰</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-400 to-purple-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">คำขอแลกเหรียญ</p>
              <p className="text-2xl font-bold">
                {exchangeRequests.filter(r => r.status === 'pending').length}
              </p>
            </div>
            <div className="text-4xl">🔄</div>
          </div>
        </div>
      </div>

      {/* ตั้งค่าข้อมูลการชำระเงิน */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">ตั้งค่าข้อมูลการชำระเงิน</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddPaymentMethod(true)}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
            >
              ➕ เพิ่มช่องทาง
            </button>
            <button
              onClick={() => setIsEditingPayment(!isEditingPayment)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              {isEditingPayment ? 'ยกเลิก' : 'แก้ไข'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(paymentConfig).map(([methodId, method]) => (
            <div key={methodId} className={`p-4 rounded-lg border ${getPaymentMethodColor(method.color)} ${
              !method.enabled ? 'opacity-50' : ''
            }`}>
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-semibold flex items-center">
                  <span className="mr-2">{method.icon}</span>
                  {method.name}
                </h4>
                <div className="flex gap-1">
                  <button
                    onClick={() => togglePaymentMethod(methodId)}
                    className={`px-2 py-1 text-xs rounded ${
                      method.enabled 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}
                    title={method.enabled ? 'ปิดการใช้งาน' : 'เปิดการใช้งาน'}
                  >
                    {method.enabled ? '✅' : '❌'}
                  </button>
                  {isEditingPayment && (
                    <button
                      onClick={() => removePaymentMethod(methodId)}
                      className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200"
                      title="ลบช่องทาง"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>

              {isEditingPayment ? (
                <div className="space-y-2">
                  {method.type === 'bank' && (
                    <>
                      <input
                        type="text"
                        placeholder="ชื่อธนาคาร"
                        value={method.bankName || ''}
                        onChange={(e) => setPaymentConfig({
                          ...paymentConfig,
                          [methodId]: { ...method, bankName: e.target.value }
                        })}
                        className="w-full p-2 text-sm border rounded"
                      />
                      <input
                        type="text"
                        placeholder="เลขที่บัญชี"
                        value={method.accountNumber || ''}
                        onChange={(e) => setPaymentConfig({
                          ...paymentConfig,
                          [methodId]: { ...method, accountNumber: e.target.value }
                        })}
                        className="w-full p-2 text-sm border rounded"
                      />
                      <input
                        type="text"
                        placeholder="ชื่อบัญชี"
                        value={method.accountName || ''}
                        onChange={(e) => setPaymentConfig({
                          ...paymentConfig,
                          [methodId]: { ...method, accountName: e.target.value }
                        })}
                        className="w-full p-2 text-sm border rounded"
                      />
                    </>
                  )}
                  {(method.type === 'mobile' || method.type === 'wallet') && (
                    <>
                      <input
                        type="text"
                        placeholder="เบอร์โทร"
                        value={method.phoneNumber || ''}
                        onChange={(e) => setPaymentConfig({
                          ...paymentConfig,
                          [methodId]: { ...method, phoneNumber: e.target.value }
                        })}
                        className="w-full p-2 text-sm border rounded"
                      />
                      <input
                        type="text"
                        placeholder="ชื่อบัญชี"
                        value={method.accountName || ''}
                        onChange={(e) => setPaymentConfig({
                          ...paymentConfig,
                          [methodId]: { ...method, accountName: e.target.value }
                        })}
                        className="w-full p-2 text-sm border rounded"
                      />
                    </>
                  )}
                  {method.type === 'other' && (
                    <>
                      <input
                        type="text"
                        placeholder="ชื่อบัญชี"
                        value={method.accountName || ''}
                        onChange={(e) => setPaymentConfig({
                          ...paymentConfig,
                          [methodId]: { ...method, accountName: e.target.value }
                        })}
                        className="w-full p-2 text-sm border rounded"
                      />
                      <input
                        type="text"
                        placeholder="หมายเลขบัญชี"
                        value={method.accountNumber || ''}
                        onChange={(e) => setPaymentConfig({
                          ...paymentConfig,
                          [methodId]: { ...method, accountNumber: e.target.value }
                        })}
                        className="w-full p-2 text-sm border rounded"
                      />
                    </>
                  )}
                  
                  {/* QR Code Upload Section */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      QR Code (สำหรับ PromptPay/พร้อมเพย์)
                    </label>
                    {method.qrCodeUrl ? (
                      <div className="space-y-2">
                        <div className="relative">
                          <img
                            src={method.qrCodeUrl}
                            alt="QR Code"
                            className="w-32 h-32 object-contain border border-gray-300 rounded"
                          />
                          <button
                            type="button"
                            onClick={() => removeQRCode(methodId)}
                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            title="ลบ QR Code"
                          >
                            ×
                          </button>
                        </div>
                        <p className="text-xs text-gray-600">คลิกเพื่อเปลี่ยน QR Code</p>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600 mb-2">
                        ยังไม่มี QR Code
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleQRCodeUpload(methodId, e.target.files[0]);
                        }
                      }}
                      className="w-full p-2 text-sm border rounded"
                      disabled={uploadingQRCode[methodId]}
                    />
                    {uploadingQRCode[methodId] && (
                      <p className="text-xs text-blue-600 mt-1">กำลังอัพโหลด...</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm">
                  {method.type === 'bank' && (
                    <>
                      <p>ธนาคาร: {method.bankName}</p>
                      <p>เลขที่บัญชี: {method.accountNumber}</p>
                      <p>ชื่อบัญชี: {method.accountName}</p>
                    </>
                  )}
                  {(method.type === 'mobile' || method.type === 'wallet') && (
                    <>
                      <p>เบอร์โทร: {method.phoneNumber}</p>
                      <p>ชื่อ: {method.accountName}</p>
                    </>
                  )}
                  {method.type === 'other' && (
                    <>
                      <p>ชื่อ: {method.accountName}</p>
                      <p>หมายเลข: {method.accountNumber}</p>
                    </>
                  )}
                  
                  {/* Display QR Code if exists */}
                  {method.qrCodeUrl && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-700 mb-2">QR Code:</p>
                      <img
                        src={method.qrCodeUrl}
                        alt="QR Code"
                        className="w-24 h-24 object-contain border border-gray-300 rounded"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {isEditingPayment && (
          <div className="mt-4 text-center">
            <button
              onClick={savePaymentConfig}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
            >
              บันทึกการตั้งค่า
            </button>
          </div>
        )}
      </div>

      {/* ตั้งค่าแพ็คเกจ T Coins */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">ตั้งค่าแพ็คเกจ T Coins</h3>
          <button
            onClick={() => setIsEditingPackages(!isEditingPackages)}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
          >
            {isEditingPackages ? 'ยกเลิก' : 'แก้ไข'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(packageConfig).map(([packageId, config]) => {
            return (
              <div key={packageId} className={`p-6 rounded-lg border ${
                config.enabled ? 'bg-gray-50 border-gray-200' : 'bg-gray-100 border-gray-300 opacity-75'
              }`}>
                <div className="flex justify-between items-center mb-4">
                  <h4 className={`font-semibold text-lg ${
                    config.enabled ? 'text-gray-800' : 'text-gray-500'
                  }`}>
                    {config.name}
                  </h4>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    config.enabled 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {config.enabled ? '✅ เปิดใช้งาน' : '❌ ปิดใช้งาน'}
                  </div>
                </div>
                {isEditingPackages ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ชื่อแพ็คเกจ (EN)
                        </label>
                        <input
                          type="text"
                          value={config.name}
                          onChange={(e) => setPackageConfig({
                            ...packageConfig,
                            [packageId]: { ...config, name: e.target.value }
                          })}
                          className="w-full p-2 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          คำอธิบาย (TH)
                        </label>
                        <input
                          type="text"
                          value={config.description}
                          onChange={(e) => setPackageConfig({
                            ...packageConfig,
                            [packageId]: { ...config, description: e.target.value }
                          })}
                          className="w-full p-2 text-sm border rounded"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ราคา (บาท)
                        </label>
                        <input
                          type="number"
                          value={config.price}
                          onChange={(e) => setPackageConfig({
                            ...packageConfig,
                            [packageId]: { ...config, price: parseInt(e.target.value) || 0 }
                          })}
                          className="w-full p-2 text-sm border rounded"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          T Coins หลัก
                        </label>
                        <input
                          type="number"
                          value={config.tcoins}
                          onChange={(e) => setPackageConfig({
                            ...packageConfig,
                            [packageId]: { ...config, tcoins: parseInt(e.target.value) || 0 }
                          })}
                          className="w-full p-2 text-sm border rounded"
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          จำนวนโบนัส
                        </label>
                        <input
                          type="number"
                          value={config.bonusAmount}
                          onChange={(e) => setPackageConfig({
                            ...packageConfig,
                            [packageId]: { ...config, bonusAmount: parseInt(e.target.value) || 0 }
                          })}
                          className="w-full p-2 text-sm border rounded"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ประเภทโบนัส
                        </label>
                        <select
                          value={config.bonusType}
                          onChange={(e) => setPackageConfig({
                            ...packageConfig,
                            [packageId]: { ...config, bonusType: e.target.value }
                          })}
                          className="w-full p-2 text-sm border rounded"
                        >
                          <option value="virtual">Bonus Coins (สงวนสิทธิ์ใช้งานในระบบเท่านั้น แลกเป็นสดเงินไม่ได้)</option>
                          <option value="real">T Coins (แลกกลับเป็นเงินสดได้)</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          สีแพ็คเกจ
                        </label>
                        <select
                          value={config.color}
                          onChange={(e) => setPackageConfig({
                            ...packageConfig,
                            [packageId]: { ...config, color: e.target.value }
                          })}
                          className="w-full p-2 text-sm border rounded"
                        >
                          <option value="silver">Silver</option>
                          <option value="gold">Gold</option>
                          <option value="emerald">Emerald</option>
                          <option value="onyx">Onyx</option>
                          <option value="purple">Purple</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          แพ็คเกจยอดนิยม
                        </label>
                        <select
                          value={config.popular ? 'true' : 'false'}
                          onChange={(e) => setPackageConfig({
                            ...packageConfig,
                            [packageId]: { ...config, popular: e.target.value === 'true' }
                          })}
                          className="w-full p-2 text-sm border rounded"
                        >
                          <option value="false">ไม่ใช่</option>
                          <option value="true">ใช่</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          สถานะการใช้งาน
                        </label>
                        <select
                          value={config.enabled ? 'true' : 'false'}
                          onChange={(e) => setPackageConfig({
                            ...packageConfig,
                            [packageId]: { ...config, enabled: e.target.value === 'true' }
                          })}
                          className={`w-full p-2 text-sm border rounded ${
                            config.enabled ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
                          }`}
                        >
                          <option value="true">เปิดใช้งาน</option>
                          <option value="false">ปิดใช้งาน</option>
                        </select>
                        <p className={`text-xs mt-1 ${config.enabled ? 'text-green-600' : 'text-red-600'}`}>
                          {config.enabled ? '✅ แพ็คเกจนี้จะแสดงให้ผู้ใช้เห็น' : '❌ แพ็คเกจนี้จะไม่แสดงให้ผู้ใช้เห็น'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-700 space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p><span className="font-semibold">ชื่อ:</span> {config.name}</p>
                        <p><span className="font-semibold">คำอธิบาย:</span> {config.description}</p>
                        <p><span className="font-semibold">ราคา:</span> ฿{config.price.toLocaleString()}</p>
                      </div>
                      <div>
                        <p><span className="font-semibold">T Coins หลัก:</span> {config.tcoins.toLocaleString()}</p>
                        <p><span className="font-semibold">โบนัส:</span> {config.bonusAmount} Bonus Coins</p>
                        <p><span className="font-semibold">ประเภทโบนัส:</span> 
                          <span className={`ml-1 font-semibold ${config.bonusType === 'virtual' ? 'text-red-600' : 'text-green-600'}`}>
                            {config.bonusType === 'virtual' ? 'Bonus Coins (สงวนสิทธิ์ใช้งานในระบบเท่านั้น แลกเป็นสดเงินไม่ได้)' : 'T Coins (แลกกลับเป็นเงินสดได้)'}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <p><span className="font-semibold">สี:</span> {config.color}</p>
                      <p><span className="font-semibold">ยอดนิยม:</span> {config.popular ? 'ใช่' : 'ไม่ใช่'}</p>
                      <p><span className="font-semibold">สถานะ:</span> 
                        <span className={`ml-1 font-semibold ${config.enabled ? 'text-green-600' : 'text-red-600'}`}>
                          {config.enabled ? '✅ เปิดใช้งาน' : '❌ ปิดใช้งาน'}
                        </span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {isEditingPackages && (
          <div className="mt-4 text-center">
            <button
              onClick={savePackageConfig}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
            >
              บันทึกการตั้งค่าแพ็คเกจ
            </button>
          </div>
        )}

        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h5 className="text-sm font-semibold text-yellow-800 mb-2">⚠️ คำแนะนำ:</h5>
          <ul className="text-xs text-yellow-700 space-y-1">
            <li>• <strong>Bonus Coins สงวนสิทธิ์ใช้งานในระบบเท่านั้น แลกเป็นสดเงินไม่ได้</strong>: ป้องกันการโกง ไม่สามารถแลกเป็นเงินได้</li>
            <li>• <strong>T Coins แลกกลับเป็นเงินสดได้</strong>: สามารถแลกเป็นเงินได้เมื่อเกิน 300 T Coins</li>
            <li>• แนะนำให้ใช้ Bonus Coins สำหรับโบนัสเพื่อความปลอดภัย</li>
          </ul>
        </div>
      </div>

      {/* ตั้งค่าค่าใช้จ่ายการตรวจสอบร้านค้า */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">ตั้งค่าค่าใช้จ่ายการตรวจสอบร้านค้า</h3>
          <button
            onClick={() => setIsEditingVerification(!isEditingVerification)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
          >
            {isEditingVerification ? 'ยกเลิก' : 'แก้ไข'}
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          {isEditingVerification ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ค่าใช้จ่ายการตรวจสอบครั้งแรก (Initial) - T Coins
                  </label>
                  <input
                    type="number"
                    value={verificationConfig.initialCost}
                    onChange={(e) => setVerificationConfig({
                      ...verificationConfig,
                      initialCost: parseInt(e.target.value) || 0
                    })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ค่าใช้จ่ายสำหรับร้านค้าที่ขอตรวจสอบครั้งแรก
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ค่าใช้จ่ายการต่ออายุ (Renewal) - T Coins
                  </label>
                  <input
                    type="number"
                    value={verificationConfig.renewalCost}
                    onChange={(e) => setVerificationConfig({
                      ...verificationConfig,
                      renewalCost: parseInt(e.target.value) || 0
                    })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ค่าใช้จ่ายสำหรับร้านค้าที่ต่ออายุการตรวจสอบ
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setIsEditingVerification(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={saveVerificationConfig}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                >
                  บันทึกการตั้งค่า
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">การตรวจสอบครั้งแรก</h4>
                <p className="text-2xl font-bold text-green-600">
                  {verificationConfig.initialCost.toLocaleString()} T Coins
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  สำหรับร้านค้าที่ขอตรวจสอบครั้งแรก
                </p>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">การต่ออายุ</h4>
                <p className="text-2xl font-bold text-blue-600">
                  {verificationConfig.renewalCost.toLocaleString()} T Coins
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  สำหรับร้านค้าที่ต่ออายุการตรวจสอบ
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* จัดการเหรียญ */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">จัดการเหรียญผู้ใช้</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* เลือกผู้ใช้ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เลือกผู้ใช้
            </label>
            <select
              value={selectedUser?.id || ''}
              onChange={(e) => {
                const user = users.find(u => u.id === e.target.value);
                setSelectedUser(user);
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">เลือกผู้ใช้</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.displayName || user.email} 
                  (Virtual: {user.totalVirtual}, Real: {user.totalReal})
                </option>
              ))}
            </select>
          </div>

          {/* ประเภทการดำเนินการ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ประเภทการดำเนินการ
            </label>
            <select
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="add">เพิ่มเหรียญ</option>
              <option value="deduct">หักเหรียญ</option>
            </select>
          </div>

          {/* ประเภทเหรียญ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ประเภทเหรียญ
            </label>
            <select
              value={coinType}
              onChange={(e) => setCoinType(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={TCOINS_TYPES.VIRTUAL}>Bonus Coins (เหรียญโบนัส)</option>
              <option value={TCOINS_TYPES.REAL}>T Coins (เหรียญจริง)</option>
            </select>
          </div>

          {/* จำนวนเหรียญ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              จำนวนเหรียญ
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="จำนวนเหรียญ"
              min="1"
            />
          </div>

          {/* เหตุผล */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เหตุผล
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="เหตุผลในการจัดการเหรียญ"
            />
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={handleCoinsAction}
            disabled={isProcessing || !selectedUser || !amount || !reason}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            {isProcessing ? 'กำลังดำเนินการ...' : `${actionType === 'add' ? 'เพิ่ม' : 'หัก'}เหรียญ`}
          </button>
        </div>
      </div>

      {/* คำขอซื้อเหรียญ */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">คำขอซื้อเหรียญ T Coins</h3>
        
        {purchaseRequests.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>ไม่มีคำขอซื้อเหรียญ</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ผู้ใช้
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    จำนวนเหรียญ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ราคา
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    วิธีชำระ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    หลักฐานการโอน
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    สถานะ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    วันที่ขอ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    จัดการ
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {purchaseRequests.map((request) => {
                  const user = users.find(u => u.id === request.userId);
                  
                  return (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user?.displayName || user?.email || request.userName || 'ไม่พบข้อมูล'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.amount} T Coins
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <p className="text-green-600 font-semibold">
                          {request.totalPrice.toLocaleString()} บาท
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.paymentMethod === 'bank_transfer' ? 'โอนธนาคาร' :
                         request.paymentMethod === 'promptpay' ? 'พร้อมเพย์' :
                         request.paymentMethod === 'true_wallet' ? 'ทรูวอลเล็ท' : request.paymentMethod}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                        <div className="truncate" title={request.paymentProof}>
                          {request.paymentProof}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                          {getStatusText(request.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.createdAt?.toDate?.()?.toLocaleDateString('th-TH') || 'ไม่ทราบ'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.status === 'pending' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handlePurchaseRequest(request.id, 'approved')}
                              className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition"
                            >
                              อนุมัติ
                            </button>
                            <button
                              onClick={() => handlePurchaseRequest(request.id, 'rejected')}
                              className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition"
                            >
                              ปฏิเสธ
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* คำขอแลกเหรียญ */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">คำขอแลกเหรียญเป็นเงิน</h3>
        
        {exchangeRequests.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>ไม่มีคำขอแลกเหรียญ</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ผู้ใช้
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    จำนวนเหรียญ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    เงินที่จะได้รับ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    สถานะ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    วันที่ขอ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    จัดการ
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {exchangeRequests.map((request) => {
                  const user = users.find(u => u.id === request.userId);
                  const fee = Math.max(request.amount * 0.05, 15);
                  const netAmount = request.amount - fee;
                  
                  return (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user?.displayName || user?.email || 'ไม่พบข้อมูล'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.amount} T Coins
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="space-y-1">
                          <p className="text-green-600 font-semibold">
                            {netAmount.toLocaleString()} บาท
                          </p>
                          <p className="text-red-600 text-xs">
                            (หักค่าธรรมเนียม {fee} บาท)
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                          {getStatusText(request.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.createdAt?.toDate?.()?.toLocaleDateString('th-TH') || 'ไม่ทราบ'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.status === 'pending' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleExchangeRequest(request.id, 'approved')}
                              className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition"
                            >
                              อนุมัติ
                            </button>
                            <button
                              onClick={() => handleExchangeRequest(request.id, 'rejected')}
                              className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition"
                            >
                              ปฏิเสธ
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal เพิ่มช่องทางการชำระเงินใหม่ */}
      {showAddPaymentMethod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">เพิ่มช่องทางการชำระเงินใหม่</h3>
              <button
                onClick={() => setShowAddPaymentMethod(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ID ช่องทาง</label>
                <input
                  type="text"
                  placeholder="เช่น: kasikorn_bank, line_pay"
                  value={newPaymentMethod.id}
                  onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, id: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อช่องทาง</label>
                <input
                  type="text"
                  placeholder="เช่น: ธนาคารกสิกรไทย, LINE Pay"
                  value={newPaymentMethod.name}
                  onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ประเภท</label>
                <select
                  value={newPaymentMethod.type}
                  onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, type: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="bank">🏦 ธนาคาร</option>
                  <option value="mobile">📱 โมบายล์</option>
                  <option value="wallet">💳 วอลเล็ท</option>
                  <option value="other">💰 อื่นๆ</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ไอคอน</label>
                <input
                  type="text"
                  placeholder="เช่น: 🏦, 📱, 💳"
                  value={newPaymentMethod.icon}
                  onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, icon: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">สี</label>
                <select
                  value={newPaymentMethod.color}
                  onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, color: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="green">🟢 เขียว</option>
                  <option value="blue">🔵 น้ำเงิน</option>
                  <option value="purple">🟣 ม่วง</option>
                  <option value="red">🔴 แดง</option>
                  <option value="yellow">🟡 เหลือง</option>
                  <option value="gray">⚫ เทา</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={newPaymentMethod.enabled}
                  onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, enabled: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="enabled" className="text-sm text-gray-700">เปิดใช้งานทันที</label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowAddPaymentMethod(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                ยกเลิก
              </button>
              <button
                onClick={addPaymentMethod}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
              >
                เพิ่มช่องทาง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoinsManagement;
